import { Request } from './request';

/**
 * 位置码管理器
 */
export class PositionCodeManager {
  private baseUrl: string;
  private appCode: string;
  private debug: boolean;
  /** 根据 pathname 缓存位置码 */
  private positionCodeCache = new Map<string, string>();
  /** 正在进行的请求 Promise，按 pathname 存储 */
  private fetchingPromises = new Map<string, Promise<string>>();

  constructor(baseUrl: string, appCode: string, debug: boolean = false) {
    this.baseUrl = baseUrl;
    this.appCode = appCode;
    this.debug = debug;
  }

  /**
   * 获取当前页面的位置码
   * 页面初始化时调用 /api/v1/position-code/ensure/{appCode}/{codeOriginal}
   */
  async getCurrentPositionCode(pathname?: string): Promise<string> {
    const currentPath = pathname || this.getCurrentPath();

    // 如果已经缓存，直接返回
    if (this.positionCodeCache.has(currentPath)) {
      const cached = this.positionCodeCache.get(currentPath)!;
      if (this.debug) {
        console.log('[MetricTracker] Using cached position code:', cached, 'for path:', currentPath);
      }
      return cached;
    }

    // 如果正在获取中，等待当前的请求完成
    if (this.fetchingPromises.has(currentPath)) {
      if (this.debug) {
        console.log('[MetricTracker] Position code fetch already in progress, waiting...');
      }
      return this.fetchingPromises.get(currentPath)!;
    }

    // 开始新的获取请求
    const fetchPromise = this.fetchPositionCode(currentPath);
    this.fetchingPromises.set(currentPath, fetchPromise);

    try {
      const result = await fetchPromise;
      return result;
    } finally {
      // 请求完成后清除 Promise（成功或失败都清除）
      this.fetchingPromises.delete(currentPath);
    }
  }

  /**
   * 实际执行位置码获取的方法
   */
  private async fetchPositionCode(pathname: string): Promise<string> {
    try {
      // URL 编码路径，处理特殊字符
      const encodedPath = encodeURIComponent(pathname);

      // 调用后端接口获取位置码
      const url = `${this.baseUrl}/api/v1/position-code/ensure/${this.appCode}/${encodedPath}`;

      if (this.debug) {
        console.log('[MetricTracker] Fetching position code:', url);
      }

      const response = await Request.get<string>(url);

      // 后端返回的是位置码完整路径（字符串），按 pathname 缓存
      const positionCode = response.data;
      this.positionCodeCache.set(pathname, positionCode);

      if (this.debug) {
        console.log('[MetricTracker] Position code obtained:', positionCode, 'for path:', pathname);
      }

      return positionCode;
    } catch (error) {
      console.error('[MetricTracker] Failed to fetch position code:', error);
      // 如果获取失败，返回默认值
      return 'unknown';
    }
  }

  /**
   * 获取当前页面路径
   * 包含 pathname，不包含 search 和 hash
   */
  private getCurrentPath(): string {
    return window.location.pathname;
  }

  /**
   * 清除缓存的位置码（用于页面切换时）
   */
  clearCache(): void {
    this.positionCodeCache.clear();
    this.fetchingPromises.clear();
    if (this.debug) {
      console.log('[MetricTracker] Position code cache cleared');
    }
  }

  /**
   * 清除指定路径的位置码缓存
   */
  clearCacheForPath(pathname: string): void {
    this.positionCodeCache.delete(pathname);
    this.fetchingPromises.delete(pathname);
    if (this.debug) {
      console.log('[MetricTracker] Position code cache cleared for path:', pathname);
    }
  }

  /**
   * 手动设置位置码（用于 SPA 路由切换场景）
   */
  setPositionCode(pc: string, pathname?: string): void {
    const path = pathname || this.getCurrentPath();
    this.positionCodeCache.set(path, pc);
  }
}
